// app/frontend/components/Notifications/notificationIconMap.tsx
//
// Shared icon + priority helpers for both NotificationDropdown (bell panel)
// and NotificationItem (full-page /notifications view). Keeping this in one
// place prevents drift when new notification types land on the backend.
//
// Coverage is derived from app/services/notification_service.rb —
// re-verify when the notification catalog grows.

import React from 'react';
import {
  IconCrown,
  IconBell,
  IconTicket,
  IconTicketOff,
  IconTarget,
  IconCalendar,
  IconCalendarEvent,
  IconCalendarPlus,
  IconCalendarQuestion,
  IconCalendarRepeat,
  IconClock,
  IconCurrencyDollar,
  IconStar,
  IconHeart,
  IconMessageCircle,
  IconUserPlus,
  IconUserCheck,
  IconUserX,
  IconFlag,
  IconClipboardText,
  IconSparkles,
  IconAlertCircle,
  IconBook,
  IconArrowBackUp,
  IconShoppingBag,
  IconTruckDelivery,
} from '@tabler/icons-react';

export type NotificationPriority = 'critical' | 'high' | 'normal' | 'low';

// Explicit type → icon map. Fall-through to <IconBell/> only when the type
// is genuinely unknown (new backend type not yet mapped here).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ICON_MAP: Record<string, React.ComponentType<any>> = {
  // Ticketing
  ticket_purchased: IconTicket,
  ticket_organizer_sale: IconTicket,
  ticket_order_cancelled: IconTicketOff,
  ticket_refunded: IconArrowBackUp,
  ticket_transfer_received: IconTicket,
  ticket_transfer_completed: IconTicket,
  ticket_transfer_requested: IconTicket,
  ticket_transfer_incoming: IconTicket,
  ticket_transfer_approved: IconTicket,
  ticket_transfer_claim: IconTicket,
  ticket_transfer_denied: IconTicketOff,
  ticket_transfer_expired: IconClock,
  ticket_transfer_cancelled: IconTicketOff,

  // Merch store
  merch_order_confirmed: IconShoppingBag,
  merch_order_shipped: IconTruckDelivery,

  // Fund-the-floor
  floor_funded_buyer: IconTarget,
  floor_funded_organizer: IconTarget,

  // Payments / money
  payment_received: IconCurrencyDollar,
  lesson_payment_sent: IconCurrencyDollar,
  lesson_payment_reminder: IconCurrencyDollar,
  stripe_connect_started: IconCurrencyDollar,
  stripe_connect_completed: IconCurrencyDollar,
  stripe_connect_stalled: IconAlertCircle,
  artist_pro_started: IconCrown,
  artist_pro_payment_failed: IconCrown,
  artist_pro_ended: IconCrown,
  star_purchase_buyer: IconStar,
  star_purchase_artist: IconStar,
  star_purchase_admin: IconStar,

  // Bookings (private lessons)
  booking_confirmed: IconCalendarEvent,
  booking_cancelled: IconCalendarEvent,
  artist_booking: IconCalendarEvent,

  // Event lifecycle
  event_new_in_area: IconCalendar,
  event_status_change: IconCalendar,
  event_bookmarked_update: IconCalendar,
  event_reminder: IconClock,
  event_organizer_approved: IconCalendarPlus,
  event_submitted: IconCalendarPlus,
  event_post_published: IconCalendar,
  organizer_new_event: IconCalendarPlus,
  new_event_created: IconCalendarPlus,
  event_rsvp_going: IconCalendarEvent,
  event_rsvp_interested: IconCalendarEvent,
  recurring_event_approved: IconCalendarPlus,
  recurring_events_generated: IconCalendarPlus,
  recurring_series_check: IconCalendarQuestion,
  recurring_series_lapsed: IconCalendarQuestion,
  recurring_series_digest: IconCalendarRepeat,

  // Social
  social_like: IconHeart,
  social_comment: IconMessageCircle,
  new_follower: IconUserPlus,
  artist_new_subscriber: IconUserPlus,
  organizer_new_subscriber: IconUserPlus,

  // Artist / organizer status
  artist_claim_approved: IconUserCheck,
  artist_claim_denied: IconUserX,
  artist_approved: IconUserCheck,
  artist_submitted: IconUserPlus,
  new_artist_created: IconUserPlus,
  organizer_approved: IconUserCheck,
  new_organization_created: IconUserPlus,
  artist_event_submission: IconCalendarPlus,

  // Content / moderation
  content_moderation: IconFlag,
  review_flagged: IconFlag,
  unknown_dance_styles_detected: IconAlertCircle,

  // Surveys
  survey_intake_received: IconClipboardText,
  survey_response_received: IconClipboardText,
  survey_response_needs_attention: IconAlertCircle,
  survey_highlight_drafted: IconClipboardText,
  survey_subject_rated: IconClipboardText,
  admin_survey_response_received: IconClipboardText,

  // Glossary / misc
  glossary_suggestion_submitted: IconBook,
  product_updates: IconSparkles,
  user_created: IconUserPlus,
};

/**
 * Returns the icon component for a given notification type. Falls back to
 * IconBell for unknown types (safe default; also indicates a mapping gap
 * worth filing).
 */
export function getNotificationIcon(type: string, size = 18, strokeWidth = 1.75): React.ReactElement {
  const IconComponent = ICON_MAP[type] || IconBell;
  return <IconComponent size={size} strokeWidth={strokeWidth} />;
}

